<template>
  <b-modal
    :visible="!!invoice"
    :title="fullyPaid ? 'Payments' : 'Add Payment'"
    size="lg"
    no-close-on-backdrop
    @hidden="$emit('close')"
  >
    <template v-if="invoice">
      <div class="ipm-summary">
        <div><span class="ipm-label">Invoice</span> <span class="ipm-mono">{{ invoice.invoice_reference }}</span></div>
        <div><span class="ipm-label">Invoice amount</span> {{ formatCurrency(invoice.amount, invoice.currency_type) }}</div>
        <div><span class="ipm-label">Payments</span> {{ formatCurrency(invoice.amount_paid, invoice.currency_type) }}</div>
        <div>
          <span class="ipm-label">Remaining balance</span>
          <strong :class="invoice.remaining_balance > 0 ? 'ipm-owing' : 'ipm-settled'">
            {{ formatCurrency(invoice.remaining_balance, invoice.currency_type) }}
          </strong>
        </div>
      </div>

      <div v-if="fullyPaid" class="ipm-muted ipm-paid-note">
        This invoice is fully paid. You can still correct a recorded payment below.
      </div>
      <b-form v-else @submit.prevent="submit">
        <b-form-group label="Amount" label-for="ipm-amount" label-size="sm">
          <b-form-input
            id="ipm-amount"
            v-model="amount"
            type="number"
            step="0.01"
            min="0.01"
            :max="invoice.remaining_balance"
            size="sm"
            required
          />
          <b-link class="ipm-fill" @click="amount = String(invoice.remaining_balance)">
            Pay the full remaining balance
          </b-link>
        </b-form-group>

        <b-form-group label="Payment date" label-for="ipm-date" label-size="sm">
          <b-form-input id="ipm-date" v-model="paymentDate" type="date" :max="today" size="sm" required />
        </b-form-group>

        <b-form-group label="Reference (optional)" label-for="ipm-reference" label-size="sm">
          <b-form-input id="ipm-reference" v-model="reference" maxlength="255" size="sm" />
        </b-form-group>

        <b-alert :show="!!error" variant="danger" class="ipm-error">{{ error }}</b-alert>
      </b-form>

      <div class="ipm-history">
        <div class="ipm-history__title">Payments recorded</div>
        <div v-if="loadingPayments" class="ipm-muted">Loading…</div>
        <div v-else-if="!payments.length" class="ipm-muted">No payments recorded yet.</div>
        <table v-else class="ipm-history__table">
          <thead>
            <tr><th>Date</th><th>Amount</th><th>Reference</th><th>Recorded by</th><th></th></tr>
          </thead>
          <tbody>
            <template v-for="p in payments">
              <tr v-if="editing && editing.id === p.id" :key="p.id" class="ipm-history__editing">
                <td>
                  <b-form-input v-model="editing.payment_date" type="date" :max="today" size="sm" aria-label="Payment date" />
                </td>
                <td>
                  <b-form-input
                    v-model="editing.amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    :max="editMax"
                    size="sm"
                    aria-label="Amount"
                  />
                </td>
                <td>
                  <b-form-input v-model="editing.reference" maxlength="255" size="sm" aria-label="Reference" />
                </td>
                <td>{{ p.recorded_by }}</td>
                <td class="ipm-history__actions">
                  <b-button size="sm" variant="primary" :disabled="editSaving || !canSaveEdit" @click="saveEdit">
                    <b-spinner v-if="editSaving" small class="mr-1" />
                    Save
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" :disabled="editSaving" @click="cancelEdit">Cancel</b-button>
                </td>
              </tr>
              <tr v-else :key="p.id">
                <td>{{ p.payment_date }}</td>
                <td>{{ formatCurrency(p.amount, invoice.currency_type) }}</td>
                <td>{{ p.reference || '—' }}</td>
                <td>{{ p.recorded_by }}</td>
                <td class="ipm-history__actions">
                  <b-link v-if="!invoice.is_archived" :disabled="!!editing || saving" @click="startEdit(p)">Edit</b-link>
                </td>
              </tr>
              <tr v-for="(edit, i) in p.edits" :key="`${p.id}-edit-${i}`" class="ipm-history__edit">
                <td colspan="5">
                  Edited {{ formatTimestamp(edit.edited_at) }} by {{ edit.edited_by }}:
                  {{ describeChanges(edit.changes) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <b-alert :show="!!editError" variant="danger" class="ipm-error mt-2">{{ editError }}</b-alert>
      </div>
    </template>

    <template #modal-footer="{ cancel }">
      <b-button size="sm" variant="outline-secondary" :disabled="saving" @click="cancel()">
        {{ fullyPaid ? 'Close' : 'Cancel' }}
      </b-button>
      <b-button v-if="!fullyPaid" size="sm" variant="primary" :disabled="saving || !canSubmit" @click="submit">
        <b-spinner v-if="saving" small class="mr-1" />
        Add Payment
      </b-button>
    </template>
  </b-modal>
</template>

<script>
// Superuser-only, one invoice at a time (LIQ-2073/2077) — InvoiceViewTable
// only opens this for a single selected invoice, and the proxy refuses
// anyone else. Lincoln re-checks the balance on submit, so an amount over
// the remaining balance is refused there even if it gets past the form.
//
// LIQ-2074: a recorded payment can be corrected in place (product chose
// that over reversing entries). Lincoln writes an audit row for every edit
// and returns each payment's edit history, shown under its row. A fully
// paid invoice opens here too, for its log and corrections only.
import { defineComponent, ref, computed, watch } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'
import { formatCurrency } from './useInvoiceExport'
import { requestErrorMessage, responseBody } from './useInvoicingSelection'

const todayIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const FIELD_LABELS = { amount: 'amount', payment_date: 'date', reference: 'reference' }

export default defineComponent({
  name: 'InvoicePaymentModal',
  props: {
    // The invoice row (as listed in the Invoice view), or null when closed.
    invoice: { type: Object, default: null },
  },
  emits: ['close', 'saved', 'updated'],
  setup(props, { emit }) {
    const amount = ref('')
    const paymentDate = ref(todayIso())
    const reference = ref('')
    const saving = ref(false)
    const error = ref('')
    const payments = ref([])
    const loadingPayments = ref(false)
    const today = todayIso()

    // The payment being edited: a copy of its row, or null.
    const editing = ref(null)
    const editSaving = ref(false)
    const editError = ref('')

    const fullyPaid = computed(() => !!props.invoice && Number(props.invoice.remaining_balance) <= 0)

    const loadPayments = async (invoiceId) => {
      loadingPayments.value = true
      try {
        const resp = await InvoicingService.getInvoicePayments(invoiceId)
        payments.value = responseBody(resp).data || []
      } catch (e) {
        payments.value = []
      } finally {
        loadingPayments.value = false
      }
    }

    // Reset only when a different invoice opens: an edit refreshes the
    // invoice's totals without closing the modal.
    watch(() => props.invoice && props.invoice.invoice_id, (invoiceId) => {
      amount.value = ''
      paymentDate.value = todayIso()
      reference.value = ''
      error.value = ''
      payments.value = []
      editing.value = null
      editError.value = ''
      if (invoiceId) loadPayments(invoiceId)
    }, { immediate: true })

    const canSubmit = computed(() => {
      const value = Number(amount.value)
      return !!props.invoice && value > 0 && value <= Number(props.invoice.remaining_balance) && !!paymentDate.value
    })

    const submit = async () => {
      if (!canSubmit.value || saving.value) return
      saving.value = true
      error.value = ''
      try {
        await InvoicingService.addInvoicePayment({
          invoice_id: props.invoice.invoice_id,
          amount: Number(amount.value).toFixed(2),
          payment_date: paymentDate.value,
          reference: reference.value,
        })
        emit('saved')
      } catch (e) {
        error.value = await requestErrorMessage(e, 'The payment could not be saved. Please try again.')
      } finally {
        saving.value = false
      }
    }

    const startEdit = (payment) => {
      editError.value = ''
      editing.value = {
        id: payment.id,
        original: payment,
        amount: String(payment.amount),
        payment_date: payment.payment_date,
        reference: payment.reference || '',
      }
    }

    const cancelEdit = () => {
      editing.value = null
      editError.value = ''
    }

    // An edited payment may use the remaining balance plus what it already holds.
    const editMax = computed(() => {
      if (!editing.value || !props.invoice) return 0
      return Number(props.invoice.remaining_balance) + Number(editing.value.original.amount)
    })

    const canSaveEdit = computed(() => {
      if (!editing.value) return false
      const value = Number(editing.value.amount)
      return value > 0 && value <= editMax.value + 1e-9 && !!editing.value.payment_date
    })

    const saveEdit = async () => {
      if (!canSaveEdit.value || editSaving.value) return
      // The modal can be closed, or switched to another invoice, while the
      // request is in flight; only touch its state if it still shows this one.
      const invoiceId = props.invoice.invoice_id
      const stillOpen = () => !!props.invoice && props.invoice.invoice_id === invoiceId
      editSaving.value = true
      editError.value = ''
      try {
        const resp = await InvoicingService.editInvoicePayment({
          invoice_id: invoiceId,
          payment_id: editing.value.id,
          amount: Number(editing.value.amount).toFixed(2),
          payment_date: editing.value.payment_date,
          reference: editing.value.reference,
        })
        const data = responseBody(resp).data || {}
        // Emitted either way so the table refreshes; the parent only applies
        // the totals to the modal if it still shows this invoice.
        if (data.changed) emit('updated', data.invoice)
        if (!stillOpen()) return
        editing.value = null
        if (data.changed) await loadPayments(invoiceId)
      } catch (e) {
        const message = await requestErrorMessage(e, 'The payment could not be updated. Please try again.')
        if (stillOpen()) editError.value = message
      } finally {
        editSaving.value = false
      }
    }

    const formatTimestamp = (iso) => (iso ? iso.slice(0, 16).replace('T', ' ') : '')

    const describeValue = (field, value) => {
      if (field === 'amount') return formatCurrency(Number(value), props.invoice.currency_type)
      return value || '—'
    }

    const describeChanges = (changes = {}) => Object.keys(changes)
      .map((field) => `${FIELD_LABELS[field] || field} ${describeValue(field, changes[field].from)} → ${describeValue(field, changes[field].to)}`)
      .join('; ')

    return {
      amount, paymentDate, reference, saving, error, payments, loadingPayments, today,
      canSubmit, submit, formatCurrency, fullyPaid,
      editing, editSaving, editError, editMax, canSaveEdit, startEdit, cancelEdit, saveEdit,
      formatTimestamp, describeChanges,
    }
  },
})
</script>

<style scoped>
.ipm-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
  font-size: 12px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #f4f6f8;
  border-radius: 6px;
}
.ipm-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #8898aa; }
.ipm-mono { font-family: monospace; }
.ipm-owing { color: #f5365c; }
.ipm-settled { color: #2dce89; }
.ipm-fill { font-size: 11px; }
.ipm-error { font-size: 12px; padding: 6px 10px; }
.ipm-paid-note { margin-bottom: 10px; }
.ipm-history { margin-top: 10px; border-top: 1px solid #e9ecef; padding-top: 10px; }
.ipm-history__title { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #32325d; margin-bottom: 6px; }
.ipm-history__table { width: 100%; font-size: 11px; }
.ipm-history__table th { font-size: 10px; color: #8898aa; font-weight: 700; text-transform: uppercase; padding: 4px 6px; }
.ipm-history__table td { padding: 4px 6px; border-top: 1px solid #f0f0f0; }
.ipm-history__actions { white-space: nowrap; text-align: right; }
.ipm-history__actions .btn + .btn { margin-left: 4px; }
.ipm-history__table tr.ipm-history__edit td { border-top: none; padding-top: 0; font-size: 10px; color: #8898aa; font-style: italic; }
.ipm-muted { font-size: 11px; color: #8898aa; }
</style>
